import { clsx } from 'clsx';
import { Btn, Card } from '../ui';
import type { tInspectionFormInput } from '../../types';
import { InspectFlowResponseIcon } from './InspectFlowResponseIcon';
import {
    buildReviewSections,
    computeReviewSummaryStats,
    formatMachineHours,
    type ReviewStatusTone,
} from './inspectionReviewUtils';
import { useInspectionSignature } from './useInspectionSignature';
import '../../css/InspectionFlowWizard.css';
import '../../css/InspectionFlowReview.css';

type InspectFlowReviewProps = {
    equipmentTitle: string;
    formSubtitle: string;
    prompts: tInspectionFormInput[];
    isSubmitting?: boolean;
    onExit: () => void;
    onGoBack: () => void;
    onSubmit: () => void;
    flowKind?: 'inspection' | 'dailyService';
};

const SUMMARY_ROWS: Array<{
    key: keyof ReturnType<typeof computeReviewSummaryStats>;
    label: string;
    tone?: ReviewStatusTone | 'neutral';
}> = [
    { key: 'totalItems', label: 'Total Items', tone: 'neutral' },
    { key: 'okCount', label: 'OK', tone: 'ok' },
    { key: 'warnCount', label: 'Warning', tone: 'warn' },
    { key: 'inopCount', label: 'Inoperative', tone: 'inop' },
    { key: 'naCount', label: 'N/A', tone: 'na' },
];

export const InspectFlowReview = ({
    equipmentTitle,
    formSubtitle,
    prompts,
    isSubmitting = false,
    onExit,
    onGoBack,
    onSubmit,
    flowKind = 'inspection',
}: InspectFlowReviewProps) => {
    const exitLabel = flowKind === 'dailyService' ? 'Exit Daily Service' : 'Exit Inspection';
    const submitLabel = flowKind === 'dailyService' ? 'Submit Record' : 'Submit Inspection';
    const submitReadyMessage =
        flowKind === 'dailyService'
            ? 'Signature saved. You can submit the record.'
            : 'Signature saved. You can submit the inspection.';
    const sections = buildReviewSections(prompts);
    const summary = computeReviewSummaryStats(prompts);
    const { canvasRef, signatureCaptured, hasDrawing, clearSignature, saveSignature, ensureSignaturePersisted } =
        useInspectionSignature();

    return (
        <main className="inspect-flow-review" aria-labelledby="inspect-flow-review-title">
            <header className="inspect-flow-wizard-top inspect-flow-review-top">
                <div className="inspect-flow-wizard-top-text">
                    <h1 id="inspect-flow-review-title" className="inspect-flow-wizard-title">
                        {equipmentTitle}
                    </h1>
                    <p className="inspect-flow-wizard-subtitle">{formSubtitle}</p>
                </div>
                <div className="inspect-flow-wizard-top-actions">
                    <span className="inspect-flow-review-complete-pill">100% Complete</span>
                    <Btn
                        variant="secondary-soft-outlined"
                        buttonText={exitLabel}
                        buttonOnClick={onExit}
                        buttonClasses="inspect-flow-wizard-exit-btn"
                    />
                </div>
            </header>

            <div className="inspect-flow-review-layout">
                <div className="inspect-flow-review-sections">
                    {sections.map((section) => (
                        <Card
                            key={section.title}
                            variant="card-lg"
                            className="inspect-flow-review-section-card card-pad-0"
                        >
                            <h2 className="inspect-flow-review-section-header">{section.title}</h2>
                            {section.isEmptyNotesSection ? (
                                <p className="inspect-flow-review-empty-notes">
                                    No additional notes recorded.
                                </p>
                            ) : (
                                <ul className="inspect-flow-review-row-list">
                                    {section.rows.map((row) => (
                                        <li key={`${section.title}-${row.label}`} className="inspect-flow-review-row">
                                            <span className="inspect-flow-review-row-label">{row.label}</span>
                                            {row.detail ? (
                                                <span className="inspect-flow-review-row-detail">{row.detail}</span>
                                            ) : (
                                                <span className="inspect-flow-review-row-detail is-empty" aria-hidden="true" />
                                            )}
                                            {row.statusTone && row.statusLabel ? (
                                                <span
                                                    className={clsx(
                                                        'inspect-flow-review-status',
                                                        `tone-${row.statusTone}`,
                                                    )}
                                                >
                                                    <span className="inspect-flow-review-status-icon" aria-hidden="true">
                                                        <InspectFlowResponseIcon tone={row.statusTone} />
                                                    </span>
                                                    {row.statusLabel}
                                                </span>
                                            ) : (
                                                <span className="inspect-flow-review-row-spacer" aria-hidden="true" />
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Card>
                    ))}
                </div>

                <aside className="inspect-flow-review-sidebar">
                    <Card variant="card-lg" className="inspect-flow-review-summary-card card-pad-0">
                        <h2 className="inspect-flow-review-panel-header">Inspection Summary</h2>
                        <dl className="inspect-flow-review-summary-list">
                            {SUMMARY_ROWS.map(({ key, label, tone }) => (
                                <div key={key} className="inspect-flow-review-summary-item">
                                    <dt
                                        className={clsx(
                                            tone && tone !== 'neutral' && `tone-${tone}`,
                                        )}
                                    >
                                        {tone && tone !== 'neutral' && (
                                            <span className="inspect-flow-review-summary-icon" aria-hidden="true">
                                                <InspectFlowResponseIcon tone={tone} />
                                            </span>
                                        )}
                                        {label}
                                    </dt>
                                    <dd>{summary[key]}</dd>
                                </div>
                            ))}
                            <div className="inspect-flow-review-summary-item inspect-flow-review-summary-item--hours">
                                <dt>Machine Hours</dt>
                                <dd>{formatMachineHours(summary.machineHours)}</dd>
                            </div>
                        </dl>
                    </Card>

                    <Card variant="card-lg" className="inspect-flow-review-sign-card card-pad-0">
                        <div className="inspect-flow-review-sign-body">
                            <fieldset className="inspect-flow-review-signature-fieldset">
                                <legend className="inspect-flow-review-panel-header inspect-flow-review-panel-header--muted">
                                    Sign to Submit
                                </legend>
                                <p id="inspect-flow-signature-instructions" className="visually-hidden">
                                    Use your pointer or touch to draw your signature in the box below, then save
                                    it before submitting.
                                </p>
                                <div className="inspect-flow-review-signature-wrap">
                                    <canvas
                                        ref={canvasRef}
                                        className="inspect-flow-review-signature-canvas"
                                        aria-label="Signature pad"
                                        aria-describedby="inspect-flow-signature-instructions"
                                    />
                                    {!hasDrawing && (
                                        <p className="inspect-flow-review-signature-placeholder" aria-hidden="true">
                                            Draw your signature here
                                        </p>
                                    )}
                                </div>
                                <div className="inspect-flow-review-signature-actions">
                                    <Btn
                                        variant="secondary-soft-outlined"
                                        buttonText="Clear"
                                        buttonOnClick={() => {
                                            void clearSignature();
                                        }}
                                    />
                                    <Btn
                                        variant="primary"
                                        buttonText={signatureCaptured ? 'Saved' : 'Save Signature'}
                                        buttonDisabled={signatureCaptured || !hasDrawing}
                                        buttonOnClick={() => {
                                            void saveSignature();
                                        }}
                                    />
                                </div>
                            </fieldset>
                            <p aria-live="polite" aria-atomic="true" className="visually-hidden">
                                {signatureCaptured ? submitReadyMessage : ''}
                            </p>
                            <Btn
                                variant="warning"
                                buttonText={isSubmitting ? 'Submitting…' : submitLabel}
                                buttonDisabled={!signatureCaptured || isSubmitting}
                                buttonOnClick={() => {
                                    void (async () => {
                                        await ensureSignaturePersisted();
                                        onSubmit();
                                    })();
                                }}
                                buttonClasses="inspect-flow-review-submit-btn"
                            />
                            <p className="inspect-flow-review-submit-hint">
                                Submitting will create an issue record for any flagged items.
                            </p>
                        </div>
                    </Card>
                </aside>
            </div>

            <div className="inspect-flow-review-footer">
                <Btn variant="secondary-soft-outlined" buttonText="← Go Back" buttonOnClick={onGoBack} />
            </div>
        </main>
    );
};
